import { useMemo } from 'react';
import { css } from '@emotion/react';
import {
  AppointmentTypeGroup,
  BookableAppointmentType,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/bookable_appointment_type.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { InfinitePaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  ChecklistField,
  DropdownField,
  SearchField,
  Text,
  transparentize,
  useDebouncedValue,
  useFormField,
} from '@frontend/design-system';
import { AvailabilityRuleFormRow, AvailabilityRuleFormSection } from '../atoms';
import { AvailabilityMode, GetAvailabilityRuleFormFieldProps } from '../types';

type ServicesFormProps = {
  locationId: string | undefined;
  getFieldProps: GetAvailabilityRuleFormFieldProps;
  providerList: { value: string; label: string }[];
  operatoryList: { value: string; label: string }[];
  availabilityMode: AvailabilityMode;
};

const INFINITE_LIST_HEIGHT = theme.spacing(16);

export const ServicesForm = ({
  locationId,
  providerList,
  operatoryList,
  getFieldProps,
  availabilityMode,
}: ServicesFormProps) => {
  const { t } = useTranslation('schedule-settings');
  const searchField = useFormField({ type: 'text' });
  const debouncedSearchValue = useDebouncedValue(searchField.value, 500);

  const infiniteQueryResult = SchedulerV3.Helpers.useGetServiceTypesV3InfiniteData({
    selectedLocationId: locationId,
    debouncedSearchValue,
    fetchOnlyServiceTypes: true,
  });
  const servicesFieldProps = getFieldProps('services');

  const selectedServices = useMemo(() => {
    const selectedServiceIds = servicesFieldProps.value || [];
    const servicesList = infiniteQueryResult.data?.pages.flatMap((page) => page.bookableAppointmentTypes) || [];
    return selectedServiceIds.map((id) => {
      const service = servicesList.find((item) => item.id === id);
      return service ? service.displayName : '';
    });
  }, [infiniteQueryResult.data, servicesFieldProps.value]);

  return (
    <AvailabilityRuleFormSection title={t('Provider & Services')}>
      <AvailabilityRuleFormRow>
        {availabilityMode === 'PROVIDER' ? (
          <DropdownField label={t('Operatory')} {...getFieldProps('operatory')} hasNonFloatingLabel>
            <DropdownField.Option key={'empty'} value={''}>
              {t('Select an operatory')}
            </DropdownField.Option>
            {operatoryList?.map((item) => (
              <DropdownField.Option key={item.value} value={item.value}>
                {item.label}
              </DropdownField.Option>
            ))}
          </DropdownField>
        ) : (
          <DropdownField
            label={t('Serviceable Provider')}
            {...getFieldProps('serviceableProvider')}
            hasNonFloatingLabel
          >
            <DropdownField.Option key={'empty'} value={''}>
              {t('Select a provider')}
            </DropdownField.Option>
            {providerList?.map((item) => (
              <DropdownField.Option key={item.value} value={item.value}>
                {item.label}
              </DropdownField.Option>
            ))}
          </DropdownField>
        )}
        <DropdownField
          label={availabilityMode === 'PROVIDER' ? t('Billable Provider') : t('Primary Provider')}
          {...getFieldProps('billableProvider')}
          hasNonFloatingLabel
        >
          <DropdownField.Option key={'empty'} value={''}>
            {t('Select a provider')}
          </DropdownField.Option>
          {providerList?.map((item) => (
            <DropdownField.Option key={item.value} value={item.value}>
              {item.label}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </AvailabilityRuleFormRow>
      <AvailabilityRuleFormRow title={t('Services')}>
        <div css={styles.servicesWrapper}>
          <SearchField
            name='availability-rule-services-search'
            hasNonFloatingLabel
            {...searchField}
            css={styles.searchField}
          />
          {selectedServices.length > 0 && (
            <Text size='small' css={styles.selectedServicesText}>
              {selectedServices.length} {t('selected')}.
            </Text>
          )}
          <ChecklistField label={''} {...getFieldProps('services')} hasNonFloatingLabel css={styles.checklistField}>
            <InfinitePaginatedList
              height={INFINITE_LIST_HEIGHT}
              infiniteQueryProps={infiniteQueryResult}
              renderListItem={({ listItem, index }) => {
                if (!isBookableAppointmentTypeItem(listItem)) return null;
                return <BookabableAppointmentOptionItem key={listItem.id} listItem={listItem} index={index} />;
              }}
              emptyStateConfig={{ type: 'schedule', header: t('No matches found') }}
            />
          </ChecklistField>
        </div>
      </AvailabilityRuleFormRow>
    </AvailabilityRuleFormSection>
  );
};

const BookabableAppointmentOptionItem = ({ listItem, index }: { listItem: BookableAppointmentType; index: number }) => {
  return (
    <ChecklistField.Option key={listItem.id} name={listItem.id} css={styles.checklistOption(index)}>
      <div css={styles.checklistOptionText}>
        <Text>{listItem.displayName}</Text>
        <Text>{listItem.duration} mins</Text>
      </div>
    </ChecklistField.Option>
  );
};

const styles = {
  servicesWrapper: css`
    display: flex;
    flex-direction: column;
    border: 1px solid ${transparentize(0.8, theme.colors.black)};
    border-radius: ${theme.borderRadius.medium};
    overflow: hidden;
  `,
  searchField: css`
    margin: ${theme.spacing(1)};
  `,
  checklistField: css`
    max-height: ${INFINITE_LIST_HEIGHT};
    overflow-y: auto;
    overflow-x: hidden;
    > label {
      margin: 0;
    }
  `,
  checklistOption: (index: number) => css`
    margin: ${theme.spacing(1, 1)};
    ${index === 0 && '&'} {
      margin-top: ${theme.spacing(0)};
    }
    label {
      width: 100%;
      margin-bottom: ${theme.spacing(0)};
      padding: ${theme.spacing(0)};
    }
  `,
  checklistOptionText: css`
    display: flex;
    width: 100%;
    justify-content: space-between;
  `,
  selectedServicesText: css`
    margin: ${theme.spacing(0, 1, 0)};
  `,
};

const isBookableAppointmentTypeItem = (
  item: BookableAppointmentType | AppointmentTypeGroup
): item is BookableAppointmentType => {
  return !('bookableAppointmentTypes' in item);
};
